import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box, TextField, Typography} from '@mui/material';
import {useFormInput} from '../../utils/utilities';
import CustomButton from '../CustomButton';
import Select from '../Select';
import {addCustomerGarage, editCustomerGarage, openModal} from '../../actions';
import {getGaragesByBuildingApi} from '../../api/garages.service';
import {fieldError, isFieldIvalid} from '../../utils/errorHandler';
import {convertSiteCode, siteCodeFromConverted} from '../../utils/siteCode';

/**
 * Optional garage data on a customer: site code, site number, car plates and the garages
 * (any number of the customer's project garages). Everything is optional; it only feeds reports.
 */
const CustomerGarageModal = ({edit}) => {
  const dispatch = useDispatch();
  const {modalProps} = useSelector(s => s.modal);
  const {customerById} = useSelector(s => s.customers);
  const {customerGaragesLoading, customerGaragesError} = useSelector(
    s => s.garages,
  );
  const existing = edit ? modalProps.customerGarage : null;
  const customerId = modalProps.customerId;
  const buildingId = customerById?.buildingId;

  const siteCode = useFormInput(existing?.siteCode || '');
  // Site code and Converted are two spellings of the same number, so each field fills in the other
  // as you type. Converted keeps its own text so partial input isn't rewritten under the cursor.
  const [convertedText, setConvertedText] = useState(
    String(convertSiteCode(existing?.siteCode) ?? ''),
  );
  const onSiteCodeChange = e => {
    const code = e.currentTarget.value;
    siteCode.manuallyChange(code);
    setConvertedText(String(convertSiteCode(code) ?? ''));
  };
  const onConvertedChange = e => {
    const text = e.currentTarget.value;
    setConvertedText(text);
    const code = siteCodeFromConverted(text);
    if (code !== null) {
      siteCode.manuallyChange(code);
    } else if (text.trim() === '') {
      siteCode.manuallyChange('');
    }
  };
  const siteCodeInvalid =
    siteCode.value.trim() !== '' && convertSiteCode(siteCode.value) === null;
  const convertedInvalid =
    convertedText.trim() !== '' && siteCodeFromConverted(convertedText) === null;
  const siteNumber = useFormInput(existing?.siteNumber || '');
  const carPlates = useFormInput(existing?.carPlates || '');
  const [garageIds, setGarageIds] = useState(existing?.garageIds || []);
  const [garages, setGarages] = useState([]);
  const [garagesLoading, setGaragesLoading] = useState(false);

  useEffect(() => {
    if (!buildingId) return;
    let cancelled = false;
    setGaragesLoading(true);
    getGaragesByBuildingApi(buildingId, {page: 1, size: 500})
      .then(res => {
        if (!cancelled) setGarages(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setGarages([]);
      })
      .finally(() => {
        if (!cancelled) setGaragesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId]);

  const onSubmit = e => {
    e.preventDefault();
    const values = {
      garageIds,
      siteCode: siteCode.value,
      siteNumber: siteNumber.value,
      carPlates: carPlates.value,
    };
    if (edit) {
      dispatch(editCustomerGarage(existing.id, customerId, values));
    } else {
      dispatch(addCustomerGarage(customerId, values));
    }
  };

  const onDelete = () => {
    dispatch(
      openModal('DeleteModal', {
        item: {...existing, customerId},
        type: 'customerGarage',
      }),
    );
  };

  const generalError =
    typeof customerGaragesError === 'string' ? customerGaragesError : '';

  return (
    <>
      <Typography alignSelf="center" variant="h6" mt="1rem" fontWeight="bold">
        {edit ? 'Edit garage data' : 'Add garage data'}
      </Typography>
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          mt="2rem"
          gap="1rem"
          sx={{
            width: {xs: 'calc(100vw - 4rem)', sm: '30rem'},
          }}>
          <TextField
            value={siteCode.value}
            onChange={onSiteCodeChange}
            label="Site code"
            error={isFieldIvalid(customerGaragesError, 'siteCode') || siteCodeInvalid}
            helperText={
              fieldError(customerGaragesError, 'siteCode') ||
              (siteCodeInvalid ? 'Digits only, e.g. 14847651' : '')
            }
          />
          <TextField
            value={convertedText}
            onChange={onConvertedChange}
            label="Converted"
            error={convertedInvalid}
            helperText={
              convertedInvalid
                ? 'Digits only, e.g. 9746979'
                : 'Type either field, the other one fills in'
            }
          />
          <TextField
            {...siteNumber}
            label="Site number"
            error={isFieldIvalid(customerGaragesError, 'siteNumber')}
            helperText={fieldError(customerGaragesError, 'siteNumber')}
          />
          <TextField
            {...carPlates}
            label="Car plates"
            error={isFieldIvalid(customerGaragesError, 'carPlates')}
            helperText={fieldError(customerGaragesError, 'carPlates')}
          />
          <Select
            multiple
            items={garages}
            value={garageIds}
            onChangeValue={setGarageIds}
            label="Garages (optional)"
            disabled={garagesLoading || garages.length === 0}
            error={isFieldIvalid(customerGaragesError, 'garageIds')}
            helperText={
              fieldError(customerGaragesError, 'garageIds') ||
              generalError ||
              (garages.length === 0 && !garagesLoading
                ? 'This project has no garages yet'
                : '')
            }
          />
          <CustomButton type="submit" loading={customerGaragesLoading}>
            {edit ? 'Save' : 'Submit'}
          </CustomButton>
          {edit && (
            <CustomButton
              color="quaternary"
              variant="outlined"
              onClick={onDelete}
              disabled={customerGaragesLoading}>
              Delete
            </CustomButton>
          )}
        </Box>
      </form>
    </>
  );
};

export default CustomerGarageModal;
